import {useQuery} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {httpGetMeasures, HttpGetMeasuresResponse} from '@/features/measures/services/measures.http.ts'
import {useState} from 'react'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {Filters} from '@/features/measures/services/types.ts'
import {z} from 'zod'

type QueryResult = z.infer<typeof HttpGetMeasuresResponse>

export const useGetMeasures = (filters: Filters) => {
    const [sort, setSort] = useState<{
        orderBy: 'publishedAt' | 'headQuarterLocation'
        orderDirection: 'asc' | 'desc'
    } | null>(null)

    const [lastSearchAfter, setLastSearchAfter] = useState<(number | string)[] | null>(null)

    const query = useQuery<QueryResult>({
        queryKey: [QUERY_KEYS.MEASURES, filters.page, filters.perPage, filters, sort, filters.search],
        queryFn: async () => {
            const result: QueryResult = await parseAxiosPromise({
                axiosPromise: httpGetMeasures({
                    page: filters.page,
                    search: filters.search || '',
                    filters: {
                        ...filters,
                        orderBy: {
                            field: sort?.orderBy,
                            direction: sort?.orderDirection
                        }
                    },
                    perPage: filters.perPage,
                    ...(lastSearchAfter && {searchAfter: lastSearchAfter})
                }),
                responseShape: HttpGetMeasuresResponse,
                onZodError: captureException
            })

            if (result.meta?.searchAfter) {
                setLastSearchAfter(result.meta.searchAfter)
            }

            return result
        },
        enabled: true,
        staleTime: 0,
        refetchOnWindowFocus: false
    })

    const remappedData = query.data?.data || []
    const meta = query.data?.meta

    return {
        ...query,
        remappedData,
        meta,
        sort,
        setSort
    }
}
